import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Minimize2 } from 'lucide-react';
import PropTypes from 'prop-types';

const FullscreenModal = ({ 
  isOpen, 
  onClose, 
  children, 
  title = '',
  viewMode = 'table'
}) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div 
        ref={modalRef}
        className="relative bg-white rounded-lg shadow-2xl transition-all transform scale-100 flex flex-col"
        style={{
          width: '95vw',
          height: '95vh',
          maxWidth: '1920px',
          maxHeight: '1080px'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg flex-shrink-0">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-gray-900">
              {title || `${viewMode === 'table' ? 'Table' : 'Chart'} View`}
            </h3>
            <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
              Fullscreen Mode
            </span>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors group"
            title="Exit fullscreen (ESC)"
          >
            <Minimize2 
              size={20} 
              className="text-gray-600 group-hover:text-gray-900"
            />
          </button>
        </div>
        
        {/* Content - takes all remaining height */}
        <div className="flex-1 overflow-auto bg-white rounded-b-lg relative">
          <div className="h-full w-full">
            {children}
          </div>
        </div>
        
        {/* Footer hint */}
        <div className="absolute bottom-4 right-4 text-xs text-gray-400 bg-white/90 px-2 py-1 rounded pointer-events-none">
          Press ESC to exit fullscreen
        </div>
      </div>
    </div>
  );

  // Use React Portal to render outside of the component hierarchy
  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

FullscreenModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  viewMode: PropTypes.string
};

export default FullscreenModal;